import React, { useMemo, useState } from 'react';
import { useData } from '../../store/DataContext';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { EyeIcon, PackagePlusIcon, PlusIcon, SearchIcon } from 'lucide-react';
import { isExpired, isExpiringWithin, stockStatus } from './pharmacyUtils';
type MedicineMasterForm = {
  genericName: string;
  brandName: string;
  strength: string;
  dosageForm: string;
  unit: string;
  packSize: string;
  category: string;
  manufacturer: string;
  buyingPrice: string;
  sellingPrice: string;
  reorderLevel: string;
  maximumStock: string;
  status: 'Active' | 'Inactive';
};
type InventoryRow = {
  id: string;
  medicineId: string;
  drugCode: string;
  medicineName: string;
  category: string;
  supplier: string;
  batchNumber: string;
  quantity: number;
  unit: string;
  reorderLevel: number;
  expiryDate: string;
  status: string;
  isBatch: boolean;
};
const initialForm: MedicineMasterForm = {
  genericName: '',
  brandName: '',
  strength: '',
  dosageForm: 'Tablet',
  unit: 'Tablet',
  packSize: '1',
  category: 'General',
  manufacturer: '',
  buyingPrice: '',
  sellingPrice: '',
  reorderLevel: '20',
  maximumStock: '100',
  status: 'Active'
};
export function InventoryList() {
  const {
    medicines,
    medicineBatches,
    suppliers,
    addMedicine
  } = useData();
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('');
  const [supplier, setSupplier] = useState('');
  const [expiry, setExpiry] = useState('all');
  const [stock, setStock] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState<MedicineMasterForm>(initialForm);
  const [selectedRow, setSelectedRow] = useState<InventoryRow | null>(null);
  const rows = useMemo(() => {
    const allRows: InventoryRow[] = medicines.flatMap(medicine => {
      const batches = medicineBatches.filter(batch => batch.medicineId === medicine.id);
      if (!batches.length) return [{
        id: `master-${medicine.id}`,
        medicineId: medicine.id,
        drugCode: medicine.drugCode,
        medicineName: medicine.name,
        category: medicine.category,
        supplier: '',
        batchNumber: 'No batch recorded',
        quantity: medicine.currentQuantity,
        unit: medicine.unit,
        reorderLevel: medicine.reorderLevel,
        expiryDate: '',
        status: stockStatus(medicine),
        isBatch: false
      }];
      return batches.map(batch => {
        const state = isExpired(batch) ? 'Expired' : isExpiringWithin(batch, 90) ? 'Near expiry' : batch.quantityRemaining <= 0 ? 'Out of stock' : medicine.currentQuantity <= medicine.reorderLevel ? 'Low stock' : 'In stock';
        return {
          id: batch.id,
          medicineId: medicine.id,
          drugCode: medicine.drugCode,
          medicineName: medicine.name,
          category: medicine.category,
          supplier: batch.supplierId || '',
          batchNumber: batch.batchNumber,
          quantity: batch.quantityRemaining,
          unit: medicine.unit,
          reorderLevel: medicine.reorderLevel,
          expiryDate: batch.expiryDate,
          status: state,
          isBatch: true
        };
      });
    });
    const normalizedQuery = query.trim().toLowerCase();
    return allRows.filter(row => {
      const matchesQuery = !normalizedQuery || `${row.drugCode} ${row.medicineName} ${row.batchNumber} ${row.category}`.toLowerCase().includes(normalizedQuery);
      const matchesCategory = !category || row.category === category;
      const matchesSupplier = !supplier || row.supplier === supplier;
      const matchesExpiry = expiry === 'all' || expiry === 'near' && row.isBatch && row.status === 'Near expiry' || expiry === 'expired' && row.isBatch && row.status === 'Expired';
      const matchesStock = !stock || row.status === stock;
      return matchesQuery && matchesCategory && matchesSupplier && matchesExpiry && matchesStock;
    }).sort((a, b) => a.medicineName.localeCompare(b.medicineName) || a.expiryDate.localeCompare(b.expiryDate));
  }, [category, expiry, medicineBatches, medicines, query, stock, supplier]);
  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    const buyingPrice = Number(form.buyingPrice);
    const sellingPrice = Number(form.sellingPrice);
    const reorderLevel = Number(form.reorderLevel);
    const maximumStock = Number(form.maximumStock);
    if (!form.genericName.trim() || !form.brandName.trim() || !form.manufacturer.trim() || !Number.isFinite(buyingPrice) || buyingPrice < 0 || !Number.isFinite(sellingPrice) || sellingPrice < 0) return setError('Generic name, brand name, manufacturer, and valid buying and selling prices are required.');
    if (!Number.isFinite(reorderLevel) || reorderLevel < 0 || !Number.isFinite(maximumStock) || maximumStock < reorderLevel) return setError('Enter a maximum stock at or above the reorder level.');
    addMedicine({
      genericName: form.genericName.trim(),
      brandName: form.brandName.trim(),
      strength: form.strength.trim() || 'Not specified',
      dosageForm: form.dosageForm,
      unit: form.unit.trim() || form.dosageForm,
      packSize: Math.max(1, Number(form.packSize) || 1),
      category: form.category.trim() || 'General',
      manufacturer: form.manufacturer.trim(),
      buyingPrice,
      sellingPrice,
      currentQuantity: 0,
      reorderLevel,
      maximumStock,
      status: form.status
    });
    setForm(initialForm);
    setShowForm(false);
    setError('');
  };
  const categories = [...new Set(medicines.map(medicine => medicine.category))].sort();
  return <div className="space-y-5" data-id="element-4484">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-id="element-4485">
        <div data-id="element-4486">
          <p className="text-sm font-semibold text-pink-600" data-id="element-4487">
            Medicine master & batches
          </p>
          <h2 className="mt-1 text-xl font-bold text-slate-950" data-id="element-4488">
            Pharmacy Inventory
          </h2>
          <p className="mt-1 text-sm text-slate-500" data-id="element-4489">
            Live batch balances are controlled by the inventory ledger. Drug
            Code and Medicine ID are generated centrally.
          </p>
        </div>
        <Button onClick={() => setShowForm(true)} className="bg-teal-600 hover:bg-teal-700" data-id="element-4490">
          <PlusIcon className="mr-2 h-4 w-4" data-id="element-4491" /> Add medicine
        </Button>
      </div>
      {showForm && <form onSubmit={submit} className="grid grid-cols-1 gap-4 rounded-xl border border-teal-200 bg-teal-50/50 p-5 md:grid-cols-2 xl:grid-cols-3" data-id="element-4492">
          <div className="flex items-center justify-between md:col-span-2 xl:col-span-3" data-id="element-4493">
            <div data-id="element-4494">
              <h3 className="font-bold text-slate-900" data-id="element-4495">
                New medicine master record
              </h3>
              <p className="mt-1 text-xs text-slate-500" data-id="element-4496">
                System-generated: Drug Code and Medicine ID.
              </p>
            </div>
            <button type="button" onClick={() => setShowForm(false)} className="text-sm font-semibold text-slate-500 hover:text-slate-800" data-id="element-4497">
              Cancel
            </button>
          </div>
          {error && <p role="alert" className="text-sm text-red-600 md:col-span-2 xl:col-span-3" data-id="element-4498">
              {error}
            </p>}
          <Input label="Generic name" required value={form.genericName} onChange={event => setForm({
        ...form,
        genericName: event.target.value
      })} data-id="element-4499" />
          <Input label="Brand name" required value={form.brandName} onChange={event => setForm({
        ...form,
        brandName: event.target.value
      })} data-id="element-4500" />
          <Input label="Strength" placeholder="e.g. 500 mg" value={form.strength} onChange={event => setForm({
        ...form,
        strength: event.target.value
      })} data-id="element-4501" />
          <Select label="Dosage form" value={form.dosageForm} onChange={event => setForm({
        ...form,
        dosageForm: event.target.value,
        unit: form.unit === form.dosageForm ? event.target.value : form.unit
      })} options={['Tablet', 'Capsule', 'Syrup', 'Suspension', 'Injection', 'Cream', 'Ointment', 'Sachet'].map(value => ({
        value,
        label: value
      }))} data-id="element-4502" />
          <Input label="Unit" value={form.unit} onChange={event => setForm({
        ...form,
        unit: event.target.value
      })} data-id="element-4503" />
          <Input label="Pack size" required type="number" min="1" value={form.packSize} onChange={event => setForm({
        ...form,
        packSize: event.target.value
      })} data-id="element-4504" />
          <Input label="Category" value={form.category} onChange={event => setForm({
        ...form,
        category: event.target.value
      })} data-id="element-4505" />
          <Input label="Manufacturer" required value={form.manufacturer} onChange={event => setForm({
        ...form,
        manufacturer: event.target.value
      })} data-id="element-4506" />
          <Select label="Record status" value={form.status} onChange={event => setForm({
        ...form,
        status: event.target.value as MedicineMasterForm['status']
      })} options={[{
        value: 'Active',
        label: 'Active'
      }, {
        value: 'Inactive',
        label: 'Inactive'
      }]} data-id="element-4507" />
          <Input label="Buying price (ZMW)" required type="number" min="0" step="0.01" value={form.buyingPrice} onChange={event => setForm({
        ...form,
        buyingPrice: event.target.value
      })} data-id="element-4508" />
          <Input label="Selling price (ZMW)" required type="number" min="0" step="0.01" value={form.sellingPrice} onChange={event => setForm({
        ...form,
        sellingPrice: event.target.value
      })} data-id="element-4509" />
          <Input label="Reorder level" required type="number" min="0" value={form.reorderLevel} onChange={event => setForm({
        ...form,
        reorderLevel: event.target.value
      })} data-id="element-4510" />
          <Input label="Maximum stock" required type="number" min="0" value={form.maximumStock} onChange={event => setForm({
        ...form,
        maximumStock: event.target.value
      })} data-id="element-4511" />
          <div className="flex items-end justify-end md:col-span-2 xl:col-span-3" data-id="element-4512">
            <Button type="submit" className="bg-teal-600 hover:bg-teal-700" data-id="element-4513">
              <PackagePlusIcon className="mr-2 h-4 w-4" data-id="element-4514" /> Create medicine
            </Button>
          </div>
        </form>}
      <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm" data-id="element-4515">
        <div className="grid grid-cols-1 gap-3 border-b border-slate-100 p-4 lg:grid-cols-5" data-id="element-4516">
          <div className="relative lg:col-span-2" data-id="element-4517">
            <SearchIcon className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" data-id="element-4518" />
            <input aria-label="Search pharmacy inventory" value={query} onChange={event => setQuery(event.target.value)} placeholder="Search drug code, medicine or batch" className="w-full rounded-md border border-slate-300 py-2 pl-9 pr-3 text-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600" data-id="element-4519" />
          </div>
          <select aria-label="Filter by category" value={category} onChange={event => setCategory(event.target.value)} className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm" data-id="element-4520">
            <option value="" data-id="element-4521">All categories</option>
            {categories.map(value => <option key={value} value={value} data-id="element-4522">
                {value}
              </option>)}
          </select>
          <select aria-label="Filter by supplier" value={supplier} onChange={event => setSupplier(event.target.value)} className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm" data-id="element-4523">
            <option value="" data-id="element-4524">All suppliers</option>
            {suppliers.map(value => <option key={value.id} value={value.id} data-id="element-4525">
                {value.name}
              </option>)}
          </select>
          <div className="flex gap-3" data-id="element-4526">
            <select aria-label="Filter by expiry" value={expiry} onChange={event => setExpiry(event.target.value)} className="min-w-0 flex-1 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm" data-id="element-4527">
              <option value="all" data-id="element-4528">All expiry dates</option>
              <option value="near" data-id="element-4529">Near expiry</option>
              <option value="expired" data-id="element-4530">Expired</option>
            </select>
            <select aria-label="Filter by stock status" value={stock} onChange={event => setStock(event.target.value)} className="min-w-0 flex-1 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm" data-id="element-4531">
              <option value="" data-id="element-4532">All stock status</option>
              <option value="In stock" data-id="element-4533">In stock</option>
              <option value="Low stock" data-id="element-4534">Low stock</option>
              <option value="Out of stock" data-id="element-4535">Out of stock</option>
              <option value="Near expiry" data-id="element-4536">Near expiry</option>
              <option value="Expired" data-id="element-4537">Expired</option>
            </select>
          </div>
        </div>
        <div className="overflow-x-auto" data-id="element-4538">
          <table className="min-w-[1100px] w-full divide-y divide-slate-100" data-id="element-4539">
            <thead className="bg-slate-50" data-id="element-4540">
              <tr data-id="element-4541">
                {['Drug Code', 'Medicine Name', 'Batch Number', 'Current Quantity', 'Unit', 'Reorder Level', 'Expiry Date', 'Status', 'Actions'].map(heading => <th key={heading} className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500" data-id="element-4542">
                    {heading}
                  </th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-4543">
              {rows.map(row => <tr key={row.id} className="align-middle" data-id="element-4544">
                  <td className="px-5 py-3.5 font-mono text-sm font-semibold text-teal-800" data-id="element-4545">
                    {row.drugCode}
                  </td>
                  <td className="px-5 py-3.5" data-id="element-4546">
                    <p className="text-sm font-semibold text-slate-900" data-id="element-4547">
                      {row.medicineName}
                    </p>
                    <p className="mt-1 text-xs text-slate-500" data-id="element-4548">
                      {row.category}
                    </p>
                  </td>
                  <td className="px-5 py-3.5 text-sm text-slate-700" data-id="element-4549">
                    {row.batchNumber}
                  </td>
                  <td className="px-5 py-3.5 text-sm font-semibold text-slate-900" data-id="element-4550">
                    {row.quantity}
                  </td>
                  <td className="px-5 py-3.5 text-sm text-slate-600" data-id="element-4551">
                    {row.unit}
                  </td>
                  <td className="px-5 py-3.5 text-sm text-slate-600" data-id="element-4552">
                    {row.reorderLevel}
                  </td>
                  <td className="px-5 py-3.5 text-sm text-slate-600" data-id="element-4553">
                    {row.expiryDate ? new Date(`${row.expiryDate}T00:00:00`).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-5 py-3.5" data-id="element-4554">
                    <Status value={row.status} data-id="element-4555" />
                  </td>
                  <td className="px-5 py-3.5" data-id="element-4556">
                    <button type="button" onClick={() => setSelectedRow(row)} className="inline-flex items-center gap-1 text-sm font-semibold text-teal-700 hover:text-teal-900 focus:outline-none focus:ring-2 focus:ring-teal-600" data-id="element-4557">
                      <EyeIcon className="h-4 w-4" data-id="element-4558" /> Details
                    </button>
                  </td>
                </tr>)}
              {!rows.length && <tr data-id="element-4559">
                  <td colSpan={9} className="px-5 py-14 text-center text-sm text-slate-500" data-id="element-4560">
                    No batch inventory matches the selected filters.
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </section>
      {selectedRow && <section aria-live="polite" className="flex flex-col gap-3 rounded-xl border border-teal-200 bg-teal-50 p-4 sm:flex-row sm:items-center sm:justify-between" data-id="element-4561">
          <div data-id="element-4562">
            <p className="text-sm font-bold text-slate-900" data-id="element-4563">
              {selectedRow.medicineName} · {selectedRow.batchNumber}
            </p>
            <p className="mt-1 text-sm text-slate-600" data-id="element-4564">
              {selectedRow.quantity} {selectedRow.unit} available · status:{' '}
              {selectedRow.status}
              {selectedRow.expiryDate ? ` · expires ${new Date(`${selectedRow.expiryDate}T00:00:00`).toLocaleDateString()}` : ''}
            </p>
          </div>
          <Button size="sm" variant="outline" onClick={() => setSelectedRow(null)} data-id="element-4565">
            Close details
          </Button>
        </section>}
    </div>;
}
function Status({
  value
}: {
  value: string;
}) {
  const classes = value === 'Out of stock' || value === 'Expired' ? 'bg-red-50 text-red-700' : value === 'Low stock' ? 'bg-amber-50 text-amber-700' : value === 'Near expiry' ? 'bg-orange-50 text-orange-700' : 'bg-emerald-50 text-emerald-700';
  return <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${classes}`} data-id="element-4566">
      {value}
    </span>;
}